<template>
  <section id="customization" class="customization-section">
    <div class="custom-container">
      <div class="section-badge">
        <span>{{ t.customization.badge }}</span>
      </div>

      <h2 class="section-title">{{ t.customization.title }}</h2>
      <span class="section-subtitle-gradient">{{ t.customization.tagline }}</span>

      <p class="section-subtitle">
        {{ t.customization.description }}
      </p>

      <!-- Interactive Tinting & Layout Previewer -->
      <div class="tint-showcase">
        <div class="tint-controls">
          <span class="control-heading">{{ t.customization.exploreAppearance }}</span>
          <div class="tint-button-group">
            <button
              v-for="(style, index) in localizedTintStyles"
              :key="index"
              @click="selectedTintIndex = index"
              class="tint-btn"
              :class="{ active: selectedTintIndex === index }"
            >
              <span class="color-dot" :style="{ background: style.color }"></span>
              <span>{{ style.name }}</span>
            </button>
          </div>
        </div>

        <div class="tint-display-card">
          <div class="tint-image-wrapper">
            <img
              :src="currentTintStyle.image"
              :alt="currentTintStyle.name"
              class="tint-img"
            />
          </div>
          <div class="tint-caption">
            <h4>{{ currentTintStyle.title }}</h4>
            <p>{{ currentTintStyle.description }}</p>
          </div>
        </div>
      </div>

      <!-- Control Center Redesign Section -->
      <div class="control-center-showcase">
        <div class="cc-header">
          <span class="card-eyebrow">{{ t.customization.ccEyebrow }}</span>
          <h3 class="cc-title">{{ t.customization.ccTitle }}</h3>
          <p class="cc-desc">
            {{ t.customization.ccDesc }}
          </p>
        </div>

        <div class="cc-tabs">
          <button
            v-for="(tab, i) in localizedCcTabs"
            :key="i"
            @click="activeCcTab = i"
            class="cc-tab-pill"
            :class="{ active: activeCcTab === i }"
          >
            <span class="cc-tab-icon">{{ tab.icon }}</span>
            <span>{{ tab.label }}</span>
          </button>
        </div>

        <div class="cc-visual-card">
          <img
            :src="currentCcImage"
            alt="iOS 18 Control Center in action"
            class="cc-full-img"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useI18n } from '@/i18n';

export default {
  name: 'CustomizationSection',
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      selectedTintIndex: 1,
      activeCcTab: 0,
      tintImages: [
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-personalize-dark-look-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-personalize-tinted-look-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-personalize-light-look-240610_inline.jpg.large.jpg'
      ],
      ccImages: [
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Control-Center-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Control-Center-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Control-Center-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Control-Center-240610_inline.jpg.large.jpg'
      ]
    };
  },
  computed: {
    localizedTintStyles() {
      return [
        {
          name: this.t.customization.styleDark,
          color: '#1c1c1e',
          title: this.t.customization.styleDark,
          description: this.t.customization.styleDarkDesc,
          image: this.tintImages[0]
        },
        {
          name: this.t.customization.styleTinted,
          color: '#007aff',
          title: this.t.customization.styleTinted,
          description: this.t.customization.styleTintedDesc,
          image: this.tintImages[1]
        },
        {
          name: this.t.customization.styleLight,
          color: '#ffffff',
          title: this.t.customization.styleLight,
          description: this.t.customization.styleLightDesc,
          image: this.tintImages[2]
        }
      ];
    },
    localizedCcTabs() {
      return [
        { label: this.t.customization.tabFavorites, icon: '★' },
        { label: this.t.customization.tabMedia, icon: '♫' },
        { label: this.t.customization.tabHome, icon: '⌂' },
        { label: this.t.customization.tabConnectivity, icon: '📶' }
      ];
    },
    currentTintStyle() {
      return this.localizedTintStyles[this.selectedTintIndex] || this.localizedTintStyles[0];
    },
    currentCcImage() {
      return this.ccImages[this.activeCcTab] || this.ccImages[0];
    }
  }
}
</script>

<style scoped>
.customization-section {
  padding: 100px 24px;
  background: #f5f5f7;
  color: #1d1d1f;
  text-align: center;
}

.custom-container {
  max-width: 1120px;
  margin: 0 auto;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.06);
  color: #1d1d1f;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 980px;
  margin-bottom: 20px;
}

.section-title {
  font-size: clamp(34px, 5.5vw, 56px);
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.07143;
  color: #1d1d1f;
  margin-bottom: 4px;
}

.section-subtitle-gradient {
  font-size: clamp(30px, 5vw, 52px);
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: 0.004em;
  background: linear-gradient(to right,
      #ff3b30,
      #ff9500,
      #b59722,
      #4c63d9,
      #187099,
      #007aff,
      #36349e,
      #3211b4);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: inline-block;
  margin-bottom: 20px;
}

.section-subtitle {
  max-width: 760px;
  margin: 0 auto 50px;
  font-size: 17px;
  line-height: 1.45;
  color: #86868b;
  font-weight: 400;
}

/* Tint Showcase */
.tint-showcase {
  background: #ffffff;
  border-radius: 28px;
  padding: 40px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
  margin-bottom: 60px;
}

.tint-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 30px;
}

.control-heading {
  font-size: 15px;
  font-weight: 500;
  color: #6e6e73;
}

.tint-button-group {
  display: flex;
  gap: 12px;
  background: #f5f5f7;
  padding: 6px;
  border-radius: 980px;
  flex-wrap: wrap;
  justify-content: center;
}

.tint-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border-radius: 980px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #6e6e73;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tint-btn.active {
  background: #ffffff;
  color: #1d1d1f;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.tint-display-card {
  max-width: 800px;
  margin: 0 auto;
}

.tint-image-wrapper {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.tint-img {
  width: 100%;
  height: auto;
  display: block;
}

.tint-caption h4 {
  font-size: 20px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 6px;
}

.tint-caption p {
  font-size: 15px;
  color: #86868b;
  margin: 0;
}

/* Control Center Showcase */
.control-center-showcase {
  background: #ffffff;
  border-radius: 28px;
  padding: 40px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
}

.cc-header {
  max-width: 700px;
  margin: 0 auto 30px;
}

.card-eyebrow {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  color: #0071e3;
  display: block;
  margin-bottom: 8px;
}

.cc-title {
  font-size: 28px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 12px;
}

.cc-desc {
  font-size: 15px;
  line-height: 1.45;
  color: #86868b;
}

.cc-tabs {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.cc-tab-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 980px;
  border: 1px solid #d2d2d7;
  background: transparent;
  color: #1d1d1f;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cc-tab-pill.active {
  background: #1d1d1f;
  color: #ffffff;
  border-color: #1d1d1f;
}

.cc-visual-card {
  max-width: 800px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
}

.cc-full-img {
  width: 100%;
  display: block;
}
</style>
